import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSessionState } from '../hooks/useSessionState';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Search, Eye, Download, Settings, Truck, FileText, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ENTITIES = [
  { key: 'UAFPL', label: 'UNITED ACCESS FLOOR PRIVATE LIMITED' },
  { key: 'UOSPL', label: 'UNITED OFFICE SYSTEM PRIVATE LIMITED' },
  { key: 'UAPL', label: 'UNITED ACOUSTIC PRIVATE LIMITED' },
];
const VENDOR_CATS = ['Stationery','Assets','Maintenance','Housekeeping','Pantry','IT Equipment','Logistics / Courier','Events / Decoration','Services'];
const STATUS_COLORS = {
  'Draft': 'bg-gray-50 text-gray-500 border-gray-200',
  'In Progress': 'bg-blue-50 text-blue-700 border-blue-200',
  'Completed': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Rejected': 'bg-red-50 text-red-700 border-red-200',
};
const StatusBadge = ({ status }) => (
  <span className={`inline-block px-2 py-px rounded border text-[10px] font-semibold tracking-wide uppercase whitespace-nowrap ${STATUS_COLORS[status] || 'bg-gray-50 text-gray-500 border-gray-200'}`}>{(status || 'DRAFT').toUpperCase()}</span>
);
const fmtDate = (d) => { if (!d) return ''; try { const p = d.split('T')[0].split('-'); const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${parseInt(p[2])} ${m[parseInt(p[1])-1]} ${p[0]}`; } catch { return d; } };

const SectionTitle = ({ children }) => <h3 className="text-sm font-semibold text-foreground border-b pb-1 mb-3 mt-4">{children}</h3>;
const Field = ({ label, children, required }) => (
  <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>{children}</div>
);

// ==================== WORKFLOW SETTINGS ====================
const WorkflowSettings = ({ getAuthHeader }) => {
  const [routing, setRouting] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [rRes, eRes] = await Promise.all([
          fetch(`${API}/procurement/routing`, { headers: getAuthHeader() }),
          fetch(`${API}/procurement/procurement-users`, { headers: getAuthHeader() }),
        ]);
        if (rRes.ok) setRouting(await rRes.json());
        if (eRes.ok) setAllEmployees(await eRes.json());
      } catch { /* */ }
      setLoading(false);
    };
    load();
  }, [getAuthHeader]);

  const getSelected = (entityKey) => {
    const r = routing.find(r => r.entity === entityKey);
    return r?.procurement_user_emails || [];
  };

  const handleSave = async (entityKey, emails) => {
    setSaving(entityKey);
    try {
      const res = await fetch(`${API}/procurement/routing`, {
        method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity: entityKey, procurement_user_emails: emails }),
      });
      if (res.ok) {
        toast.success(`Routing saved for ${entityKey}`);
        const rRes = await fetch(`${API}/procurement/routing`, { headers: getAuthHeader() });
        if (rRes.ok) setRouting(await rRes.json());
      }
    } catch { toast.error('Failed'); }
    setSaving(null);
  };

  if (loading) return <div className="p-6"><Skeleton className="h-32 w-full" /></div>;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">Assign procurement users for each entity. These users will receive and process vendor onboarding & PO requests.</p>
      {ENTITIES.map(ent => {
        const selected = getSelected(ent.key);
        return (
          <Card key={ent.key} className="bg-gray-50/80 border-gray-200">
            <CardHeader className="pb-2 pt-4"><CardTitle className="text-sm">{ent.key} — {ent.label}</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label className="text-xs">Procurement Users</Label>
                <div className="max-h-40 overflow-y-auto border rounded p-2 space-y-1 bg-white">
                  {allEmployees.map(emp => (
                    <label key={emp.email} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-accent rounded px-1 py-0.5">
                      <Checkbox checked={selected.includes(emp.email)} onCheckedChange={(checked) => {
                        const next = checked ? [...selected, emp.email] : selected.filter(e => e !== emp.email);
                        handleSave(ent.key, next);
                      }} className="h-3.5 w-3.5" />
                      <span>{emp.full_name}</span><span className="text-muted-foreground">({emp.role})</span>
                    </label>
                  ))}
                </div>
                {selected.length > 0 && <p className="text-[10px] text-muted-foreground">{selected.length} user(s) assigned</p>}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// ==================== MAIN PAGE ====================
export default function ProcurementPage() {
  const { getAuthHeader, employee } = useAuth();
  const role = employee?.role || 'Employee';
  const isSuperAdmin = role === 'Super Admin';
  const canCreate = ['Admin Team', 'Admin Head', 'Super Admin'].includes(role);
  const hasAccess = ['Admin Team', 'Admin Head', 'Super Admin', 'Management'].includes(role) || true; // procurement users checked via API

  const [mainTab, setMainTab] = useSessionState('procurement:mainTab', 'vendor-onboarding');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusRemarks, setStatusRemarks] = useState('');
  const [adminHeads, setAdminHeads] = useState([]);

  // Filters (persisted to sessionStorage)
  const [filterEntity, setFilterEntity] = useSessionState('procurement:filterEntity', '');
  const [filterVendor, setFilterVendor] = useSessionState('procurement:filterVendor', '');
  const [filterCategory, setFilterCategory] = useSessionState('procurement:filterCategory', '');
  const [filterStatus, setFilterStatus] = useSessionState('procurement:filterStatus', '');

  // Form
  const emptyForm = {
    entity: '', vendor_name: '', vendor_address: '', city: '', state: '', pincode: '', cin_number: '',
    contact_person_name: '', vendor_mobile: '', vendor_email: '', telephone_1: '', telephone_2: '', fax: '',
    gst_number: '', pan_number: '', vendor_category: '',
    beneficiary_name: '', bank_name: '', branch_name: '', account_type: '', bank_account_number: '', ifsc_code: '', payment_terms: '',
    doc_pan_card: null, doc_gst_certificate: null, doc_certificate_of_incorporation: null, doc_cancelled_cheque: null, doc_msme_certificate: null, doc_aadhar_card: null,
    poc_from_united: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterEntity) params.set('entity', filterEntity);
      if (filterVendor) params.set('vendor_name', filterVendor);
      if (filterCategory) params.set('category', filterCategory);
      if (filterStatus) params.set('status', filterStatus);
      const res = await fetch(`${API}/procurement/vendor-onboarding?${params}`, { headers: getAuthHeader() });
      if (res.ok) setRequests(await res.json());
    } catch { /* */ }
    setLoading(false);
  }, [getAuthHeader, filterEntity, filterVendor, filterCategory, filterStatus]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);
  useEffect(() => {
    const loadHeads = async () => {
      try { const r = await fetch(`${API}/procurement/admin-heads`, { headers: getAuthHeader() }); if (r.ok) setAdminHeads(await r.json()); } catch { /* */ }
    };
    loadHeads();
  }, [getAuthHeader]);

  const handleFileUpload = async (field, file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/upload`, { method: 'POST', headers: getAuthHeader(), body: fd });
      if (res.ok) { const d = await res.json(); setForm(p => ({ ...p, [field]: d.url || d.file_url })); toast.success('Uploaded'); }
      else toast.error('Upload failed');
    } catch { toast.error('Upload failed'); }
  };

  const handleCreate = async () => {
    if (!form.entity || !form.vendor_name || !form.contact_person_name || !form.vendor_mobile || !form.gst_number || !form.pan_number) {
      toast.error('Please fill all mandatory fields'); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/procurement/vendor-onboarding`, {
        method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      if (res.ok) { toast.success('Vendor onboarding submitted'); setCreateOpen(false); setForm(emptyForm); fetchRequests(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;
    if (newStatus === 'Rejected' && !statusRemarks.trim()) { toast.error('Remarks required for rejection'); return; }
    try {
      const res = await fetch(`${API}/procurement/vendor-onboarding/${statusDialog.request_id}/status`, {
        method: 'PUT', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, remarks: statusRemarks }),
      });
      if (res.ok) { toast.success('Status updated'); setStatusDialog(null); setNewStatus(''); setStatusRemarks(''); fetchRequests(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed'); }
  };

  const downloadDoc = async (url, name) => {
    try {
      const res = await fetch(`${BACKEND}${url}`, { headers: getAuthHeader() });
      if (res.ok) { const blob = await res.blob(); const u = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = u; a.download = name; document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(u); document.body.removeChild(a); }
    } catch { toast.error('Download failed'); }
  };

  const clearFilters = () => { setFilterEntity(''); setFilterVendor(''); setFilterCategory(''); setFilterStatus(''); };
  const hasFilters = filterEntity || filterVendor || filterCategory || filterStatus;

  return (
    <Layout>
      <div className="space-y-5" data-testid="procurement-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Procurement</h1>
            <p className="text-muted-foreground text-sm mt-0.5">Vendor onboarding & purchase orders</p>
          </div>
        </div>

        <Tabs value={mainTab} onValueChange={setMainTab}>
          <TabsList className="h-9">
            <TabsTrigger value="vendor-onboarding" className="text-xs" data-testid="tab-vendor-onboarding"><Truck className="w-3.5 h-3.5 mr-1" />Vendor Onboarding</TabsTrigger>
            <TabsTrigger value="raise-po" className="text-xs" data-testid="tab-raise-po"><FileText className="w-3.5 h-3.5 mr-1" />Raise PO</TabsTrigger>
            {isSuperAdmin && <TabsTrigger value="settings" className="text-xs" data-testid="tab-workflow-settings"><Settings className="w-3.5 h-3.5 mr-1" />Workflow Settings</TabsTrigger>}
          </TabsList>

          {/* ===== VENDOR ONBOARDING ===== */}
          <TabsContent value="vendor-onboarding" className="mt-4">
            <Card className="bg-gray-50/80 border-gray-200">
              <CardHeader className="pb-3 pt-4 px-4">
                <div className="flex items-center justify-between">
                  <div><CardTitle className="text-base">Vendor Onboarding</CardTitle><CardDescription>{requests.length} request(s)</CardDescription></div>
                  {canCreate && <Button size="sm" className="h-8 text-xs" onClick={() => setCreateOpen(true)} data-testid="create-vo-btn"><Plus className="w-3.5 h-3.5 mr-1" />New Request</Button>}
                </div>
                {/* Filters */}
                <div className="flex flex-wrap items-center gap-2 pt-3">
                  <Select value={filterEntity} onValueChange={v => setFilterEntity(v === '_all' ? '' : v)}>
                    <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue placeholder="Entity" /></SelectTrigger>
                    <SelectContent><SelectItem value="_all">All Entities</SelectItem>{ENTITIES.map(e => <SelectItem key={e.key} value={e.key}>{e.key}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="relative"><Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" /><Input placeholder="Vendor name..." value={filterVendor} onChange={e => setFilterVendor(e.target.value)} className="pl-7 h-8 w-[160px] text-xs" /></div>
                  <Select value={filterCategory} onValueChange={v => setFilterCategory(v === '_all' ? '' : v)}>
                    <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent><SelectItem value="_all">All Categories</SelectItem>{VENDOR_CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={v => setFilterStatus(v === '_all' ? '' : v)}>
                    <SelectTrigger className="w-[120px] h-8 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent><SelectItem value="_all">All Status</SelectItem>{['In Progress','Completed','Rejected','Draft'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  {hasFilters && <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={clearFilters}><X className="w-3 h-3 mr-1" />Clear</Button>}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div> :
                  requests.length === 0 ? <div className="text-center py-12 text-muted-foreground"><Truck className="w-10 h-10 mx-auto mb-2 opacity-30" /><p className="text-sm">No vendor onboarding requests</p></div> : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow className="bg-gray-100 dark:bg-zinc-800 border-b border-gray-200">
                        <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">ID</TableHead>
                        <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Date</TableHead>
                        <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Entity</TableHead>
                        <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Vendor Name</TableHead>
                        <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Category</TableHead>
                        <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Contact</TableHead>
                        <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</TableHead>
                        <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Created By</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {requests.map((r, idx) => (
                          <TableRow key={r.request_id} className={`h-10 hover:bg-accent/40 cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}`} onClick={() => setDetailDialog(r)}>
                            <TableCell className="py-2 text-[11px] font-mono text-blue-600">{r.request_id}</TableCell>
                            <TableCell className="py-2 text-[12px] whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                            <TableCell className="py-2 text-[12px]">{r.entity}</TableCell>
                            <TableCell className="py-2 text-[12px] font-medium max-w-[160px] truncate">{r.vendor_name}</TableCell>
                            <TableCell className="py-2 text-[11px] text-muted-foreground max-w-[140px] truncate">{Array.isArray(r.vendor_category) ? (r.vendor_category || []).join(', ') : (r.vendor_category || '')}</TableCell>
                            <TableCell className="py-2 text-[11px] text-muted-foreground">{r.contact_person_name}</TableCell>
                            <TableCell className="py-2"><StatusBadge status={r.status} /></TableCell>
                            <TableCell className="py-2 text-[11px] text-muted-foreground">{(r.created_by || '').split(' ').slice(0,2).join(' ')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== RAISE PO (PLACEHOLDER) ===== */}
          <TabsContent value="raise-po" className="mt-4">
            <Card className="bg-gray-50/80 border-gray-200">
              <CardContent className="py-16 text-center text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Raise PO — Coming Soon</p>
                <p className="text-sm mt-1">Purchase Order creation will be available in the next update.</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== WORKFLOW SETTINGS ===== */}
          {isSuperAdmin && (
            <TabsContent value="settings" className="mt-4">
              <WorkflowSettings getAuthHeader={getAuthHeader} />
            </TabsContent>
          )}
        </Tabs>

        {/* ===== CREATE DIALOG ===== */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Vendor Onboarding</DialogTitle></DialogHeader>
            <div className="space-y-1">
              <SectionTitle>Company Details</SectionTitle>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Entity" required><Select value={form.entity} onValueChange={v => setForm(p => ({...p, entity: v}))}><SelectTrigger className="h-8 text-xs" data-testid="vo-entity"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{ENTITIES.map(e => <SelectItem key={e.key} value={e.key}>{e.key} — {e.label}</SelectItem>)}</SelectContent></Select></Field>
                <Field label="Vendor Name" required><Input value={form.vendor_name} onChange={e => setForm(p => ({...p, vendor_name: e.target.value}))} className="h-8 text-xs" data-testid="vo-vendor-name" /></Field>
                <Field label="CIN Number"><Input value={form.cin_number} onChange={e => setForm(p => ({...p, cin_number: e.target.value}))} className="h-8 text-xs" /></Field>
              </div>
              <div className="grid grid-cols-4 gap-3 mt-2">
                <Field label="Address"><Input value={form.vendor_address} onChange={e => setForm(p => ({...p, vendor_address: e.target.value}))} className="h-8 text-xs" /></Field>
                <Field label="City"><Input value={form.city} onChange={e => setForm(p => ({...p, city: e.target.value}))} className="h-8 text-xs" /></Field>
                <Field label="State"><Input value={form.state} onChange={e => setForm(p => ({...p, state: e.target.value}))} className="h-8 text-xs" /></Field>
                <Field label="Pincode"><Input value={form.pincode} onChange={e => setForm(p => ({...p, pincode: e.target.value}))} className="h-8 text-xs" /></Field>
              </div>

              <SectionTitle>Contact Details</SectionTitle>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Contact Person" required><Input value={form.contact_person_name} onChange={e => setForm(p => ({...p, contact_person_name: e.target.value}))} className="h-8 text-xs" data-testid="vo-contact" /></Field>
                <Field label="Mobile" required><Input value={form.vendor_mobile} onChange={e => setForm(p => ({...p, vendor_mobile: e.target.value}))} className="h-8 text-xs" data-testid="vo-mobile" /></Field>
                <Field label="Email"><Input value={form.vendor_email} onChange={e => setForm(p => ({...p, vendor_email: e.target.value}))} className="h-8 text-xs" /></Field>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <Field label="Telephone 1"><Input value={form.telephone_1} onChange={e => setForm(p => ({...p, telephone_1: e.target.value}))} className="h-8 text-xs" /></Field>
                <Field label="Telephone 2"><Input value={form.telephone_2} onChange={e => setForm(p => ({...p, telephone_2: e.target.value}))} className="h-8 text-xs" /></Field>
                <Field label="Fax"><Input value={form.fax} onChange={e => setForm(p => ({...p, fax: e.target.value}))} className="h-8 text-xs" /></Field>
              </div>

              <SectionTitle>Business & Tax Details</SectionTitle>
              <div className="grid grid-cols-2 gap-3">
                <Field label="GST Number" required><Input value={form.gst_number} onChange={e => setForm(p => ({...p, gst_number: e.target.value}))} className="h-8 text-xs" data-testid="vo-gst" /></Field>
                <Field label="PAN Number" required><Input value={form.pan_number} onChange={e => setForm(p => ({...p, pan_number: e.target.value}))} className="h-8 text-xs" data-testid="vo-pan" /></Field>
              </div>

              <SectionTitle>Vendor Category</SectionTitle>
              <div className="w-full md:w-1/2">
                <Select value={form.vendor_category} onValueChange={v => setForm(p => ({ ...p, vendor_category: v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="vo-category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {VENDOR_CATS.map(cat => (
                      <SelectItem key={cat} value={cat} data-testid={`vo-category-${cat.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <SectionTitle>Bank & Payment Details</SectionTitle>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Beneficiary Name"><Input value={form.beneficiary_name} onChange={e => setForm(p => ({...p, beneficiary_name: e.target.value}))} className="h-8 text-xs" /></Field>
                <Field label="Bank Name"><Input value={form.bank_name} onChange={e => setForm(p => ({...p, bank_name: e.target.value}))} className="h-8 text-xs" /></Field>
                <Field label="Branch"><Input value={form.branch_name} onChange={e => setForm(p => ({...p, branch_name: e.target.value}))} className="h-8 text-xs" /></Field>
              </div>
              <div className="grid grid-cols-4 gap-3 mt-2">
                <Field label="Account Type"><Select value={form.account_type} onValueChange={v => setForm(p => ({...p, account_type: v}))}><SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="Savings">Savings</SelectItem><SelectItem value="Current">Current</SelectItem></SelectContent></Select></Field>
                <Field label="Account Number"><Input value={form.bank_account_number} onChange={e => setForm(p => ({...p, bank_account_number: e.target.value}))} className="h-8 text-xs" /></Field>
                <Field label="IFSC Code"><Input value={form.ifsc_code} onChange={e => setForm(p => ({...p, ifsc_code: e.target.value}))} className="h-8 text-xs" /></Field>
                <Field label="Payment Terms"><Input value={form.payment_terms} onChange={e => setForm(p => ({...p, payment_terms: e.target.value}))} className="h-8 text-xs" /></Field>
              </div>

              <SectionTitle>Certificates & Documents</SectionTitle>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'doc_pan_card', label: 'PAN Card', required: true },
                  { key: 'doc_gst_certificate', label: 'GST Certificate' },
                  { key: 'doc_certificate_of_incorporation', label: 'Certificate of Incorporation' },
                  { key: 'doc_cancelled_cheque', label: 'Cancelled Cheque' },
                  { key: 'doc_msme_certificate', label: 'MSME Certificate' },
                  { key: 'doc_aadhar_card', label: 'Aadhar Card (Individual)' },
                ].map(doc => (
                  <Field key={doc.key} label={doc.label} required={doc.required}>
                    <div className="flex items-center gap-2">
                      <Input type="file" className="h-8 text-xs flex-1" onChange={e => { if (e.target.files?.[0]) handleFileUpload(doc.key, e.target.files[0]); }} />
                      {form[doc.key] && <span className="text-[10px] text-emerald-600 whitespace-nowrap">Uploaded</span>}
                    </div>
                  </Field>
                ))}
              </div>

              <SectionTitle>Internal Reference</SectionTitle>
              <div className="grid grid-cols-2 gap-3">
                <Field label="POC from United Group (Admin Head)">
                  <Select value={form.poc_from_united} onValueChange={v => setForm(p => ({...p, poc_from_united: v}))}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{adminHeads.map(h => <SelectItem key={h.email} value={h.full_name}>{h.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" size="sm" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCreate} disabled={saving} data-testid="submit-vo-btn">{saving ? 'Submitting...' : 'Submit'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== DETAIL DIALOG ===== */}
        <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            {detailDialog && (<>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{detailDialog.request_id} <StatusBadge status={detailDialog.status} /></DialogTitle>
                <DialogDescription>Created by {detailDialog.created_by} on {fmtDate(detailDialog.created_at)}</DialogDescription>
              </DialogHeader>
              <div className="space-y-1 text-sm">
                <SectionTitle>Company Details</SectionTitle>
                <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                  <div><span className="text-muted-foreground text-[10px]">Entity</span><p className="text-xs">{detailDialog.entity}</p></div>
                  <div><span className="text-muted-foreground text-[10px]">Vendor</span><p className="text-xs font-medium">{detailDialog.vendor_name}</p></div>
                  <div><span className="text-muted-foreground text-[10px]">CIN</span><p className="text-xs">{detailDialog.cin_number || '-'}</p></div>
                  <div className="col-span-2"><span className="text-muted-foreground text-[10px]">Address</span><p className="text-xs">{[detailDialog.vendor_address, detailDialog.city, detailDialog.state, detailDialog.pincode].filter(Boolean).join(', ') || '-'}</p></div>
                </div>
                <SectionTitle>Contact</SectionTitle>
                <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                  <div><span className="text-muted-foreground text-[10px]">Contact Person</span><p className="text-xs">{detailDialog.contact_person_name}</p></div>
                  <div><span className="text-muted-foreground text-[10px]">Mobile</span><p className="text-xs">{detailDialog.vendor_mobile}</p></div>
                  <div><span className="text-muted-foreground text-[10px]">Email</span><p className="text-xs">{detailDialog.vendor_email || '-'}</p></div>
                </div>
                <SectionTitle>Business & Tax</SectionTitle>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <div><span className="text-muted-foreground text-[10px]">GST</span><p className="text-xs font-mono">{detailDialog.gst_number}</p></div>
                  <div><span className="text-muted-foreground text-[10px]">PAN</span><p className="text-xs font-mono">{detailDialog.pan_number}</p></div>
                </div>
                <SectionTitle>Categories</SectionTitle>
                <div className="flex flex-wrap gap-1">{(Array.isArray(detailDialog.vendor_category) ? detailDialog.vendor_category : (detailDialog.vendor_category ? [detailDialog.vendor_category] : [])).map(c => <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>)}</div>
                <SectionTitle>Bank Details</SectionTitle>
                <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                  <div><span className="text-muted-foreground text-[10px]">Beneficiary</span><p className="text-xs">{detailDialog.beneficiary_name || '-'}</p></div>
                  <div><span className="text-muted-foreground text-[10px]">Bank</span><p className="text-xs">{detailDialog.bank_name || '-'}</p></div>
                  <div><span className="text-muted-foreground text-[10px]">Account</span><p className="text-xs">{detailDialog.bank_account_number || '-'} ({detailDialog.account_type || '-'})</p></div>
                  <div><span className="text-muted-foreground text-[10px]">IFSC</span><p className="text-xs font-mono">{detailDialog.ifsc_code || '-'}</p></div>
                  <div><span className="text-muted-foreground text-[10px]">Payment Terms</span><p className="text-xs">{detailDialog.payment_terms || '-'}</p></div>
                </div>
                <SectionTitle>Documents</SectionTitle>
                <div className="grid grid-cols-3 gap-2">
                  {[{k:'doc_pan_card',l:'PAN Card'},{k:'doc_gst_certificate',l:'GST Cert'},{k:'doc_certificate_of_incorporation',l:'COI'},{k:'doc_cancelled_cheque',l:'Cheque'},{k:'doc_msme_certificate',l:'MSME'},{k:'doc_aadhar_card',l:'Aadhar'}].map(d => (
                    <div key={d.k}>
                      {detailDialog[d.k] ? <Button variant="outline" size="sm" className="h-7 text-[10px] w-full justify-start" onClick={() => downloadDoc(detailDialog[d.k], `${detailDialog.request_id}_${d.l}`)}><Download className="w-3 h-3 mr-1" />{d.l}</Button> : <span className="text-[10px] text-muted-foreground">{d.l}: —</span>}
                    </div>
                  ))}
                </div>
                {detailDialog.poc_from_united && <><SectionTitle>Internal</SectionTitle><p className="text-xs">POC: {detailDialog.poc_from_united}</p></>}
                {detailDialog.status_history?.length > 0 && (<>
                  <SectionTitle>Status History</SectionTitle>
                  {detailDialog.status_history.map((h, i) => (
                    <div key={i} className="text-[11px] text-muted-foreground border-l-2 border-gray-200 pl-3 py-1">
                      <span className="font-medium text-foreground">{h.by}</span> set to <StatusBadge status={h.status} /> on {fmtDate(h.at?.split('T')[0])}
                      {h.remarks && <p className="mt-0.5 italic">"{h.remarks}"</p>}
                    </div>
                  ))}
                </>)}
              </div>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => { setStatusDialog(detailDialog); setDetailDialog(null); setNewStatus(''); setStatusRemarks(''); }} data-testid="update-status-btn">Update Status</Button>
              </DialogFooter>
            </>)}
          </DialogContent>
        </Dialog>

        {/* ===== STATUS UPDATE DIALOG ===== */}
        <Dialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Update Status — {statusDialog?.request_id}</DialogTitle></DialogHeader>
            <div className="space-y-3 py-1">
              <Field label="New Status">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={`Remarks${newStatus === 'Rejected' ? ' *' : ''}`}>
                <Textarea value={statusRemarks} onChange={e => setStatusRemarks(e.target.value)} placeholder="Add remarks..." rows={3} className="text-sm" data-testid="vo-status-remarks" />
              </Field>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setStatusDialog(null)}>Cancel</Button>
              <Button size="sm" onClick={handleStatusUpdate} disabled={!newStatus} data-testid="confirm-status-btn">Update</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
